import { useMemo, useState } from 'react';
import PlanningMatrix from '../../components/capacity/PlanningMatrix';
import HiringGapsSummary from '../../components/capacity/HiringGapsSummary';
import ProjectsCapacitySummary from '../../components/capacity/ProjectsCapacitySummary';
import { usePlanningMatrix, useHiringGaps, useProjectsCapacitySummary } from '../../hooks/useCapacity';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import type { CapacityRole } from '../../services/api/capacity';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function addMonths(month: string, count: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y!, m! - 1 + count, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PlanningMatrixPage() {
  const [fromMonth, setFromMonth] = useState(getCurrentMonth());
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const toMonth = addMonths(fromMonth, 11);

  const { data: matrixData, isLoading } = usePlanningMatrix(fromMonth, toMonth);
  const { data: gapsData } = useHiringGaps(fromMonth, toMonth);
  const { data: projectsData } = useProjectsCapacitySummary(fromMonth, toMonth);

  const matrix = matrixData?.data;
  const gaps = gapsData?.data ?? [];
  const projects = projectsData?.data ?? [];

  // Derive the set of roles present on returned people so the chips reflect
  // what's actually in the data (instead of hitting an extra /api/roles GET).
  const distinctRoles = useMemo<CapacityRole[]>(() => {
    if (!matrix?.people) return [];
    const seen = new Map<number, CapacityRole>();
    for (const p of matrix.people) {
      for (const r of p.roleTags) {
        if (!seen.has(r.id)) seen.set(r.id, r);
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [matrix?.people]);

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId],
    );
  };

  const filteredPeople = useMemo(() => {
    if (!matrix?.people) return [];
    if (selectedRoleIds.length === 0) return matrix.people;
    return matrix.people.filter((p) => p.roleTags.some((r) => selectedRoleIds.includes(r.id)));
  }, [matrix?.people, selectedRoleIds]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Capacity Planning</h1>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setFromMonth(addMonths(fromMonth, -12))}>
            &larr; Prev Year
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setFromMonth(getCurrentMonth())}>
            Current
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setFromMonth(addMonths(fromMonth, 12))}>
            Next Year &rarr;
          </Button>
        </div>
      </div>

      {distinctRoles.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Filter by role:</span>
          {distinctRoles.map((r) => {
            const active = selectedRoleIds.includes(r.id);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => toggleRole(r.id)}
                className={
                  active
                    ? 'px-2.5 py-1 text-xs font-medium rounded-full border border-primary-300 bg-primary-50 text-primary-700'
                    : 'px-2.5 py-1 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }
              >
                {r.name}
              </button>
            );
          })}
          {selectedRoleIds.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedRoleIds([])}
              className="px-2.5 py-1 text-xs font-medium text-red-600 hover:text-red-700"
            >
              Clear ({selectedRoleIds.length})
            </button>
          )}
          {selectedRoleIds.length > 0 && (
            <span className="text-xs text-gray-500">
              Showing {filteredPeople.length} of {matrix?.people.length ?? 0} people
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : matrix ? (
        <>
          <PlanningMatrix months={matrix.months} people={filteredPeople} />
          <ProjectsCapacitySummary projects={projects} months={matrix.months} />
          <HiringGapsSummary gaps={gaps} />
        </>
      ) : (
        <p className="text-gray-500">No capacity data available.</p>
      )}
    </div>
  );
}
